import TextInput from '../TextInput';
import TextInputWithInlineLabel from '../TextInputWithInlineLabel.jsx';
import React, { useEffect, useRef, useState } from 'react';
import useBusinessPostApi from '../../lib/useBusinessPostApi.js';
import useDelete from '../../lib/useDelete.jsx';
import * as Toast from '../../lib/Toast.js';
import {
    setBusinessInfo,
    useBusinessInfo,
    useUserInfoQuery,
} from '../../lib/Queries';
import { useNavigate } from 'react-router-dom';
import Loading from '../Loading';
import Modal from '../Modal';
import InfoMessageBlock from '~/components/InfoMessageBlock';
import OptionToggle from '~/components/OptionToggle.jsx';

const ShareBusinessButton = ({ business }) => {
    const { save, isSaving } = useBusinessPostApi(`/share`);

    function doShare(disable) {
        const isDisabled = !!disable;
        save({
            disable: isDisabled,
        }).then(result => {
            Toast.success(`Successfully ${isDisabled ? 'disabled' : 'enabled'} sharing`);
            setBusinessInfo(result);
        });
    }

    const token = business?.sharing_token;
    const shareUrl =
        new URL(document.location).origin + `/clone/${business.id}/${token}`;

    return (
        <div className="p-4 border rounded-lg w-full flex-1 flex flex-col">
            {!!token && (
                <div>
                    <div className="input-label">
                        Business Template Share Link
                    </div>
                    <div className="mb-2">
                        <input
                            type="text"
                            className="w-full mb-1"
                            readOnly={true}
                            value={shareUrl}
                        />
                        <div className="text-gray-500 text-[12px] ml-2 mb-2">
                            Share this link with other people to allow them to duplicate this business setup.
                        </div>
                        <div className="flex flex-wrap gap-2 max-w-lg">
                            <button
                                type="button"
                                className="btn-light-pill w-full xs:w-auto"
                                onClick={() =>
                                    navigator.clipboard.writeText(shareUrl)
                                }
                            >
                                Copy
                            </button>
                            <button
                                type="button"
                                className="btn-pink-pill w-full xs:w-auto"
                                onClick={() => doShare(true)}
                            >
                                Disable
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {!token && (
                <>
                    <div>Share Template</div>
                    <div className="text-gray-500 text-sm mb-4">
                        Share a link for others to copy this business's settings without client data.
                    </div>
                    <button
                        type="button"
                        className="btn-light-pill w-full xs:!w-auto xs:self-start mt-auto"
                        onClick={() => doShare(false)}
                        disabled={isSaving}
                    >
                        {isSaving ? 'Enabling...' : 'Share'}
                    </button>
                </>
            )}
        </div>
    );
};

const BusinessSettings = ({}) => {
    const containerRef = useRef();

    const { isLoading, data, refetch } = useBusinessInfo();
    const { business } = data || {};
    const { refetch: refetchUserInfo } = useUserInfoQuery();

    const navigate = useNavigate();

    const [businessName, setBusinessName] = useState('');
    const [subdomain, setSubdomain] = useState('');
    const [physicalAddress, setPhysicalAddress] = useState('');
    const [testMode, setTestMode] = useState(false);

    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const { isSaving, save } = useBusinessPostApi(`/update-business`);
    const { isSaving: isDuplicating, save: saveDuplicate } =
        useBusinessPostApi(`/duplicate`);
    const { isDeleting, saveDelete } = useDelete(`/delete`, {
        prompt: 'Are you REALLY sure you want to delete this business? This is permanent and cannot be undone.',
    });
    const { isDeleting: isWiping, saveDelete: saveWipe } = useDelete(`/wipe`, {
        prompt: 'Are you REALLY sure you want to wipe all clients, events, and form submissions for this business? This is permanent and cannot be undone.',
    });

    useEffect(() => {
        if (business) {
            setBusinessName(business.name || '');
            setSubdomain(business.subdomain || '');
            setPhysicalAddress(business.physical_address || '');
            setTestMode(!!business.test_mode);
        }
    }, [JSON.stringify(business)]);

    function submit(ev) {
        ev.preventDefault();

        save({
            name: businessName,
            subdomain: subdomain,
            physical_address: physicalAddress,
            test_mode: testMode,
        }).then(() => {
            Toast.success('Successfully saved');
            refetchUserInfo();
            refetch();
        });
    }

    function duplicate() {
        saveDuplicate().then(result => {
            navigate(`/business/${result.id}`);
        });
    }

    function doDelete() {
        if (
            prompt(
                'Are you sure you want to permanently delete this business?\n\nPlease type "DELETE" in all caps without quotes to confirm',
            ) === 'DELETE'
        ) {
            saveDelete().then(result => {
                setShowDeleteModal(false);
                navigate('/');
                Toast.success(
                    `Successfully deleted business ${business?.name}`,
                );
            });
        }
    }

    function doWipe() {
        if (
            prompt(
                'Are you sure you want permanently clear all data from this business?\n\nPlease type "WIPE" in all caps without quotes to confirm',
            ) === 'WIPE'
        ) {
            saveWipe().then(result => {
                Toast.success(`Successfully wiped data`);
                setShowDeleteModal(false);
            });
        }
    }

    const isPaidAccount = !business?.is_trial && business?.plan !== 'free';

    return (
        <div>
            {(isLoading || !business) && <Loading />}
            {!(isLoading || !business) && (
                <form onSubmit={submit} className="">
                    {data?.hipaa && (
                        <InfoMessageBlock
                            showIcon={true}
                            className="max-w-2xl mb-6"
                        >
                            This business is marked as needing to comply with
                            HIPAA. A Business Associate Agreement (BAA) should
                            be signed, and you are ultimately responsible for
                            using Breely in a manner compliant with your
                            business's policies related to HIPAA.
                        </InfoMessageBlock>
                    )}
                    <TextInput
                        value={businessName}
                        onChange={setBusinessName}
                        label="Business Name"
                        className="mb-4"
                    />

                    <div className="mb-4">
                        <TextInput
                            value={physicalAddress}
                            onChange={setPhysicalAddress}
                            label="Business Physical Address"
                            placeholder="123 Main St, City, State 12345"

                            inputClassName="max-w-lg"
                            description="This address will be displayed in emails for CANSPAM compliance"
                        />
                        <div className="text-xs text-gray-500 ml-4">
                            We show contact information in email footers to comply with
                            international spam laws
                        </div>
                    </div>


                    <div className="max-w-lg mb-4" ref={containerRef}>
                        <TextInputWithInlineLabel
                            value={subdomain}
                            onChange={setSubdomain}
                            suffix={
                                <span
                                    style={{
                                        marginRight:
                                            Math.max(
                                                0,
                                                containerRef.current
                                                    ?.offsetWidth -
                                                    120 -
                                                    subdomain.length * 10,
                                            ) + 'px',
                                    }}
                                >
                                    .breely.com
                                </span>
                            }
                            label="Subdomain"
                            id="subdomain"
                            inputClassName=""
                        />
                    </div>

                    <OptionToggle
                        value={testMode}
                        onChange={setTestMode}
                        toggleFirst={true}
                        title="Test Mode"
                        description="Test Mode disables sending of emails and texts, so your clients will not receive any notifications"
                        className="max-w-lg mb-6"
                    />

                    <div className="flex flex-wrap items-center justify-between max-w-lg gap-2 mt-6 mb-10">
                        <button type="submit" className="btn-save w-full xs:w-auto">
                            {isSaving ? 'Saving...' : 'Save'}
                        </button>
                    </div>

                    <div className="input-label">Additional Options</div>
                    <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-4 w-full xs:w-auto max-w-3xl">
                        <div className="p-4 border rounded-lg w-full flex-1 flex flex-col">
                            <div>Duplicate Business</div>
                            <div className="text-gray-500 text-sm mb-4">
                                Duplicate all settings without client data.
                            </div>
                            <button
                                type="button"
                                className="btn-light-pill w-full xs:!w-auto xs:self-start mt-auto"
                                onClick={duplicate}
                            >
                                {isDuplicating ? 'Duplicating...' : 'Duplicate'}
                            </button>
                        </div>

                        <ShareBusinessButton business={business} />

                        <div className="p-4 border rounded-lg w-full flex-1 flex flex-col">
                            <div>Delete Business</div>
                            <div className="text-gray-500 text-sm mb-4">
                                Deleting this business or wipe data.
                            </div>
                            <button
                                type="button"
                                className="btn-pink-pill w-full xs:!w-auto xs:self-start mt-auto"
                                onClick={() => setShowDeleteModal(true)}
                            >
                                {isDeleting ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                        <Modal
                            isOpen={showDeleteModal}
                            onClose={() => setShowDeleteModal(false)}
                        >
                            <h2>Delete</h2>
                            <div className="flex flex-wrap xs:flex-nowrap gap-6 mt-6">
                                <div>
                                    {isPaidAccount && (
                                        <div>
                                            <div className="">
                                                Unable to delete this
                                                business, you have a paid
                                                subscription.
                                            </div>
                                            <div className="">
                                                Please cancel your
                                                subscription in Settings
                                                &gt; Billing &gt; Manage
                                                Billing then return here to
                                                delete this business.
                                            </div>
                                        </div>
                                    )}
                                    {!isPaidAccount && (
                                        <>
                                            <button
                                                type="button"
                                                className="btn-pink-pill-lg w-full xs:w-auto"
                                                onClick={() => doDelete()}
                                            >
                                                {isDeleting
                                                    ? 'Deleting...'
                                                    : 'Delete Account'}
                                            </button>
                                            <div className="mt-2 text-sm text-gray-500">
                                                Permanently delete the
                                                entire account, all settings
                                                and data will be removed
                                            </div>
                                        </>
                                    )}
                                </div>

                                <div>
                                    <button
                                        type="button"
                                        className="btn-pink-pill-lg w-full xs:w-auto"
                                        onClick={() => doWipe()}
                                    >
                                        {isWiping
                                            ? 'Wiping...'
                                            : 'Wipe Data'}
                                    </button>
                                    <div className="mt-2 text-sm text-gray-500">
                                        Delete all clients, events, and form
                                        submissions but keep setup.
                                    </div>
                                </div>
                            </div>
                        </Modal>
                    </div>

                </form>
            )}
        </div>
    );
};

export default BusinessSettings;
